/**
 * <rewards-account> - the signed-in "Kat's Rewards" (Paw Points) module rendered
 * inside the account menu (header popover + mobile drawer). Ported from the
 * design prototype (kats-rewards-web.html, "Account page rewards module") and
 * adapted to the theme's Web Component + design-token conventions.
 *
 * Data comes ONLY from window.CKCAuth (assets/custom-auth.js):
 *   - onChange(fn)         → sign-in/out; loads the summary when signed in
 *   - getRewardsSummary()  → { balance, lifetime, ladder, activity, vouchers }
 *   - redeemReward(rung, requestId) → { voucher, summary }  (idempotent per id)
 *
 * The ladder (thresholds / values) is server-defined - always rendered from the
 * payload, never hardcoded. Redeeming mints a single-use PAW-XXXXXX code
 * (60-day expiry, one per checkout, no stacking).
 */
// Recent-activity preview length on the main screen (matches the account
// modal's order-history preview). Anything past this moves to the "Show more"
// full-activity screen.
const ACTIVITY_PREVIEW = 3;

class RewardsAccount extends HTMLElement {
  connectedCallback() {
    this.innerHTML = this.template();
    this.controller = new AbortController();
    const signal = this.controller.signal;

    this.refs = {
      root: this.querySelector('[data-rw-root]'),
      loading: this.querySelector('[data-rw-state="loading"]'),
      error: this.querySelector('[data-rw-state="error"]'),
      ready: this.querySelector('[data-rw-state="ready"]'),
      screenMain: this.querySelector('[data-rw-screen="main"]'),
      screenRedeem: this.querySelector('[data-rw-screen="redeem"]'),
      screenActivity: this.querySelector('[data-rw-screen="activity"]'),
      balance: this.querySelector('[data-rw-balance]'),
      lifetime: this.querySelector('[data-rw-lifetime]'),
      trail: this.querySelector('[data-rw-trail]'),
      hint: this.querySelector('[data-rw-hint]'),
      ladder: this.querySelector('[data-rw-ladder]'),
      vouchersCard: this.querySelector('[data-rw-vouchers]'),
      voucherList: this.querySelector('[data-rw-voucher-list]'),
      activity: this.querySelector('[data-rw-activity]'),
      activityAll: this.querySelector('[data-rw-activity-all]'),
      activityMore: this.querySelector('[data-rw-activity-more]'),
      overlay: this.querySelector('[data-rw-overlay]'),
      confirm: this.querySelector('[data-rw-confirm]'),
      confirmTitle: this.querySelector('[data-rw-confirm-title]'),
      confirmMsg: this.querySelector('[data-rw-confirm-msg]'),
      reveal: this.querySelector('[data-rw-reveal]'),
      revealDesc: this.querySelector('[data-rw-reveal-desc]'),
      revealCode: this.querySelector('[data-rw-reveal-code]'),
      revealNote: this.querySelector('[data-rw-reveal-note]'),
      revealApply: this.querySelector('[data-rw-reveal-apply]'),
      redeemError: this.querySelector('[data-rw-redeem-error]'),
    };

    this.summary = null;
    this.pending = null; // { rung, requestId }
    this.loading = false;
    this.screen = 'main'; // main | redeem | activity (in-module "pages")

    this.addEventListener('click', this.onClick.bind(this), { signal });

    const auth = window.CKCAuth;
    if (!auth) {
      this.refs.root.hidden = true;
      return;
    }
    this.unsubscribe = auth.onChange(this.onAuth.bind(this));
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.unsubscribe) this.unsubscribe();
  }

  onAuth(user, ready) {
    if (!ready) {
      this.setState('loading');
      this.refs.root.hidden = false;
      return;
    }
    if (!user) {
      // Signed out - hide the whole module (its container is hidden too, but be
      // explicit so it never flashes stale data). Reset back to the main screen
      // so a re-open (and the host modal's section visibility) starts clean.
      this.refs.root.hidden = true;
      this.summary = null;
      this.closeOverlay();
      this.showScreen('main');
      return;
    }
    this.refs.root.hidden = false;
    if (!this.summary) this.loadSummary();
  }

  async loadSummary() {
    if (this.loading) return;
    this.loading = true;
    this.setState('loading');
    try {
      const summary = await window.CKCAuth.getRewardsSummary();
      this.summary = summary;
      this.renderReady(summary, true);
      this.showScreen('main'); // fresh load always starts on the main screen
      this.setState('ready');
    } catch (e) {
      this.setState('error');
    } finally {
      this.loading = false;
    }
  }

  setState(state) {
    if (this.refs.loading) this.refs.loading.hidden = state !== 'loading';
    if (this.refs.error) this.refs.error.hidden = state !== 'error';
    if (this.refs.ready) this.refs.ready.hidden = state !== 'ready';
  }

  // ---- rendering ----
  renderReady(summary, animate) {
    const balance = Number(summary.balance) || 0;
    const ladder = Array.isArray(summary.ladder) ? summary.ladder : [];
    const vouchers = Array.isArray(summary.vouchers) ? summary.vouchers : [];
    const activity = Array.isArray(summary.activity) ? summary.activity : [];

    this.refs.balance.textContent = String(balance);
    if (this.refs.lifetime) {
      const lifetime = Number(summary.lifetime);
      if (lifetime > 0) {
        this.refs.lifetime.textContent = `${lifetime.toLocaleString()} earned all-time`;
        this.refs.lifetime.hidden = false;
      } else {
        this.refs.lifetime.hidden = true;
      }
    }

    this.renderTrail(balance, ladder, animate);
    this.renderHint(balance, ladder);
    this.renderLadder(balance, ladder);
    this.renderVouchers(vouchers);
    this.renderActivity(activity);
  }

  maxRung(ladder) {
    return ladder.length ? Number(ladder[ladder.length - 1].pts) || 0 : 0;
  }

  renderTrail(balance, ladder, animate) {
    const el = this.refs.trail;
    if (!el) return;
    const max = this.maxRung(ladder);
    // Rungs/track live in an inner wrapper inset by half a dot on each side
    // (see .rw-trail__inner) so the end checkpoints — the first chip and the
    // final golden cookie — sit within the module width instead of overflowing.
    el.innerHTML = '<div class="rw-trail__inner"><div class="rw-trail__track"></div><div class="rw-trail__fill"></div></div>';
    const inner = el.querySelector('.rw-trail__inner');
    const fill = el.querySelector('.rw-trail__fill');
    ladder.forEach((rung, i) => {
      const pts = Number(rung.pts) || 0;
      const golden = i === ladder.length - 1;
      const node = document.createElement('div');
      node.className = 'rw-trail__rung' + (balance >= pts ? ' is-reached' : '') + (golden ? ' is-golden' : '');
      node.style.left = (max ? (pts / max) * 100 : 0) + '%';
      node.innerHTML =
        '<div class="rw-trail__dot">' +
        (golden ? this.cookieImg(22) : this.chipSvg(14)) +
        '</div><span class="rw-trail__pts">' + pts + '</span>';
      inner.appendChild(node);
    });
    const pct = max ? Math.min(1, balance / max) * 100 : 0;
    if (fill) {
      if (animate) {
        requestAnimationFrame(() => requestAnimationFrame(() => { fill.style.width = pct + '%'; }));
      } else {
        fill.style.width = pct + '%';
      }
    }
  }

  renderHint(balance, ladder) {
    const el = this.refs.hint;
    if (!el) return;
    const next = ladder.find((r) => (Number(r.pts) || 0) > balance) || null;
    const unlocked = ladder.filter((r) => (Number(r.pts) || 0) <= balance);
    const ready = unlocked.length ? unlocked[unlocked.length - 1] : null;
    if (!ladder.length) {
      el.textContent = '';
      return;
    }
    if (!next) {
      el.textContent = 'You made it to the end of the trail. The Golden Cookie is ready!';
    } else if (ready) {
      el.textContent =
        `${ready.title} is unlocked, or keep walking: ${(Number(next.pts) || 0) - balance} pts to ${next.title.toLowerCase()}.`;
    } else {
      el.textContent = `${(Number(next.pts) || 0) - balance} pts to ${next.title.toLowerCase()}.`;
    }
  }

  renderLadder(balance, ladder) {
    const el = this.refs.ladder;
    if (!el) return;
    el.innerHTML = '';
    ladder.forEach((rung, i) => {
      const pts = Number(rung.pts) || 0;
      const golden = i === ladder.length - 1;
      const unlocked = balance >= pts;
      const row = document.createElement('div');
      row.className = 'rw-rung' + (golden ? ' is-golden' : '');
      const right = unlocked
        ? `<button type="button" class="rw-btn rw-btn--primary rw-btn--sm" data-rw-action="redeem" data-rung="${this.escape(rung.id)}">Redeem</button>`
        : `<span class="rw-rung__locked">${pts - balance} pts to go</span>`;
      const face = rung.faceValue ? ` · ${this.escape(rung.faceValue)} value` : '';
      row.innerHTML =
        `<div class="rw-rung__icon">${golden ? this.cookieImg(28) : this.chipSvg(18)}</div>` +
        '<div class="rw-rung__body">' +
        `<div class="rw-rung__title">${this.escape(rung.title)}</div>` +
        `<div class="rw-rung__meta">${pts} pts${face}</div>` +
        (rung.description ? `<div class="rw-rung__desc">${this.escape(rung.description)}</div>` : '') +
        '</div>' + right;
      el.appendChild(row);
    });
  }

  renderVouchers(vouchers) {
    const card = this.refs.vouchersCard;
    const list = this.refs.voucherList;
    if (!card || !list) return;
    card.hidden = vouchers.length === 0;
    list.innerHTML = '';
    vouchers.forEach((v) => {
      const row = document.createElement('div');
      row.className = 'rw-voucher';
      const expiry = v.expiresAt ? ` · expires ${this.formatDate(v.expiresAt)}` : '';
      row.innerHTML =
        '<div class="rw-voucher__body">' +
        `<div class="rw-voucher__code">${this.escape(v.code)}</div>` +
        `<div class="rw-voucher__desc">${this.escape(v.description || 'Reward')}${expiry}</div>` +
        '</div>' +
        '<div class="rw-voucher__actions">' +
        `<button type="button" class="rw-btn rw-btn--outline rw-btn--sm" data-rw-action="copy" data-code="${this.escape(v.code)}">Copy</button>` +
        `<a class="rw-voucher__apply" href="/discount/${encodeURIComponent(v.code)}?redirect=/cart">Apply to cart</a>` +
        '</div>';
      list.appendChild(row);
    });
  }

  // Renders the recent-activity preview (main screen, capped at ACTIVITY_PREVIEW)
  // and the full list (activity screen), then toggles the "Show more" link.
  renderActivity(activity) {
    const preview = this.refs.activity;
    if (preview) {
      if (!activity.length) {
        preview.innerHTML = '<p class="rw-muted">No activity yet. Your first order will earn Paw Points.</p>';
      } else {
        preview.innerHTML = activity.slice(0, ACTIVITY_PREVIEW).map((a) => this.activityRow(a)).join('');
      }
    }
    if (this.refs.activityMore) {
      this.refs.activityMore.hidden = activity.length <= ACTIVITY_PREVIEW;
    }
    if (this.refs.activityAll) {
      this.refs.activityAll.innerHTML = activity.length
        ? activity.map((a) => this.activityRow(a)).join('')
        : '<p class="rw-muted">No activity yet. Your first order will earn Paw Points.</p>';
    }
  }

  activityRow(a) {
    const pts = Number(a.pts) || 0;
    return (
      '<div class="rw-activity">' +
      '<div class="rw-activity__body">' +
      `<div class="rw-activity__label">${this.escape(a.label)}</div>` +
      (a.at ? `<div class="rw-activity__when">${this.formatDate(a.at)}</div>` : '') +
      '</div>' +
      `<span class="rw-activity__delta${pts < 0 ? ' is-neg' : ''}">${pts > 0 ? '+' : ''}${pts} pts</span>` +
      '</div>'
    );
  }

  // Swap between the in-module "pages" (main / redeem / activity). Emits a
  // bubbling event so the host account modal can hide its other sections and
  // make a sub-page read as a full page (mirrors the order-history flow).
  showScreen(screen) {
    this.screen = screen;
    if (this.refs.screenMain) this.refs.screenMain.hidden = screen !== 'main';
    if (this.refs.screenRedeem) this.refs.screenRedeem.hidden = screen !== 'redeem';
    if (this.refs.screenActivity) this.refs.screenActivity.hidden = screen !== 'activity';
    this.dispatchEvent(new CustomEvent('rewards:screen', { bubbles: true, detail: { screen } }));
  }

  // ---- redeem flow (inline sheet over the module; no top-layer modal so it
  // works inside the popover/drawer) ----
  onClick(event) {
    const trigger = event.target.closest('[data-rw-action]');
    if (!trigger || !this.contains(trigger)) return;
    const action = trigger.getAttribute('data-rw-action');

    if (action === 'open-redeem') {
      event.preventDefault();
      this.showScreen('redeem');
    } else if (action === 'open-activity') {
      event.preventDefault();
      this.showScreen('activity');
    } else if (action === 'screen-back') {
      event.preventDefault();
      this.showScreen('main');
    } else if (action === 'redeem') {
      event.preventDefault();
      this.openConfirm(trigger.getAttribute('data-rung'));
    } else if (action === 'confirm-cancel') {
      event.preventDefault();
      this.closeOverlay();
    } else if (action === 'confirm-go' || action === 'redeem-retry') {
      event.preventDefault();
      this.doRedeem(trigger);
    } else if (action === 'reveal-done') {
      event.preventDefault();
      this.closeOverlay();
    } else if (action === 'retry') {
      event.preventDefault();
      this.loadSummary();
    } else if (action === 'copy') {
      event.preventDefault();
      this.copyCode(trigger.getAttribute('data-code'), trigger);
    }
  }

  openConfirm(rungId) {
    const ladder = (this.summary && this.summary.ladder) || [];
    const rung = ladder.find((r) => r.id === rungId);
    if (!rung) return;
    // One requestId per redemption intent, reused across retries.
    this.pending = { rung, requestId: Date.now() + '-' + Math.random().toString(36).slice(2, 8) };
    if (this.refs.confirmTitle) {
      this.refs.confirmTitle.textContent = `Redeem ${Number(rung.pts) || 0} Paw Points?`;
    }
    if (this.refs.confirmMsg) {
      this.refs.confirmMsg.textContent =
        `You'll get a single-use code for: ${rung.title}. Codes last 60 days, work on one order, ` +
        "and can't be combined with other discounts.";
    }
    if (this.refs.redeemError) this.refs.redeemError.hidden = true;
    if (this.refs.confirm) this.refs.confirm.hidden = false;
    if (this.refs.reveal) this.refs.reveal.hidden = true;
    if (this.refs.overlay) this.refs.overlay.hidden = false;
  }

  async doRedeem(trigger) {
    if (!this.pending) return;
    const { rung, requestId } = this.pending;
    if (this.refs.redeemError) this.refs.redeemError.hidden = true;
    if (trigger) trigger.disabled = true;
    try {
      const result = await window.CKCAuth.redeemReward(rung.id, requestId);
      const voucher = (result && result.voucher) || {};
      // Refresh the module from the authoritative returned summary.
      if (result && result.summary) {
        this.summary = result.summary;
        this.renderReady(result.summary, true);
        this.setState('ready');
      }
      this.pending = null; // success → drop the idempotency id
      this.showReveal(voucher, rung);
    } catch (e) {
      // Keep this.pending (same requestId) so a retry is idempotent.
      if (this.refs.redeemError) {
        this.refs.redeemError.textContent =
          (e && e.message) || "That didn't go through. Check your points and try again.";
        this.refs.redeemError.hidden = false;
      }
    } finally {
      if (trigger) trigger.disabled = false;
    }
  }

  showReveal(voucher, rung) {
    const code = voucher.code || '';
    if (this.refs.revealDesc) this.refs.revealDesc.textContent = voucher.description || (rung && rung.title) || 'Reward unlocked';
    if (this.refs.revealCode) this.refs.revealCode.textContent = code || 'PAW-––––––';
    if (this.refs.revealNote) {
      const by = voucher.expiresAt ? ` by ${this.formatDate(voucher.expiresAt)}` : '';
      this.refs.revealNote.textContent =
        `Enter this code at checkout${by}. One code per order. It's saved under Available rewards too.`;
    }
    if (this.refs.revealApply && code) {
      this.refs.revealApply.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
      this.refs.revealApply.hidden = false;
    } else if (this.refs.revealApply) {
      this.refs.revealApply.hidden = true;
    }
    // Wire the reveal's copy button to this code.
    const copyBtn = this.querySelector('[data-rw-reveal-copy]');
    if (copyBtn) copyBtn.setAttribute('data-code', code);
    if (this.refs.confirm) this.refs.confirm.hidden = true;
    if (this.refs.reveal) this.refs.reveal.hidden = false;
    if (this.refs.overlay) this.refs.overlay.hidden = false;
  }

  closeOverlay() {
    if (this.refs && this.refs.overlay) this.refs.overlay.hidden = true;
    this.pending = null;
  }

  async copyCode(text, btn) {
    if (!text) return;
    const ok = await this.copyText(text);
    if (btn) {
      const orig = btn.textContent;
      btn.textContent = ok ? 'Copied ✓' : 'Copy failed';
      setTimeout(() => { btn.textContent = orig; }, 1600);
    }
  }

  async copyText(text) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      /* fall through */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const done = document.execCommand('copy');
      document.body.removeChild(ta);
      return done;
    } catch (e) {
      return false;
    }
  }

  // ---- helpers ----
  formatDate(iso) {
    if (!iso) return '';
    // Accept both YYYY-MM-DD and full ISO strings.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  escape(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  chipSvg(size) {
    return `<svg width="${size}" height="${size}" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="#5b3a1e"/></svg>`;
  }

  // Asset URL for the real cookie photo (cookie-only.png), injected via Liquid
  // asset_url into CKC_AUTH_CONFIG (and mirrored onto CKCAuth.config). Read from
  // both so it's available even before custom-auth.js finishes loading.
  cookieUrl() {
    return (
      (window.CKCAuth && window.CKCAuth.config && window.CKCAuth.config.rewardsCookieUrl) ||
      (window.CKC_AUTH_CONFIG && window.CKC_AUTH_CONFIG.rewardsCookieUrl) ||
      ''
    );
  }

  // The Golden Cookie graphic: prefers the real cookie photo, falling back to
  // the drawn SVG cookie if the asset URL isn't configured.
  cookieImg(size) {
    const url = this.cookieUrl();
    if (url) {
      return `<img class="rw-cookie-img" src="${this.escape(url)}" width="${size}" height="${size}" alt="" aria-hidden="true">`;
    }
    return this.cookieSvg(size);
  }

  cookieSvg(size) {
    return (
      `<svg width="${size}" height="${size}" viewBox="0 0 200 200" aria-hidden="true">` +
      '<circle cx="100" cy="100" r="88" fill="#d9a05b"/>' +
      '<circle cx="72" cy="76" r="12" fill="#5b3a1e"/>' +
      '<circle cx="132" cy="90" r="10" fill="#5b3a1e"/>' +
      '<circle cx="95" cy="130" r="13" fill="#5b3a1e"/>' +
      '</svg>'
    );
  }

  template() {
    return `
      <div class="rw" data-rw-root hidden>
        <div class="rw__state" data-rw-state="loading">
          <p class="rw-muted">Loading your Paw Points…</p>
        </div>

        <div class="rw__state" data-rw-state="error" hidden>
          <p class="rw-muted">We couldn't load your rewards right now.</p>
          <button type="button" class="rw-btn rw-btn--outline rw-btn--sm" data-rw-action="retry">Try again</button>
        </div>

        <div class="rw__state" data-rw-state="ready" hidden>
          <!-- MAIN SCREEN: balance, trail, redeem CTA, vouchers, recent activity -->
          <div class="rw__screen" data-rw-screen="main">
            <div class="rw__balance">
              <span class="rw__balance-num" data-rw-balance>0</span>
              <span class="rw__balance-unit">Paw Points</span>
            </div>
            <p class="rw-muted rw__lifetime" data-rw-lifetime hidden></p>

            <div class="rw__trail" data-rw-trail></div>
            <p class="rw__hint" data-rw-hint></p>

            <button type="button" class="rw-btn rw-btn--primary rw__redeem-cta" data-rw-action="open-redeem">
              Redeem Paw Points
              <span class="rw__redeem-cta-arrow" aria-hidden="true">→</span>
            </button>

            <div class="rw__block" data-rw-vouchers hidden>
              <h4 class="rw__block-title">Available rewards</h4>
              <p class="rw-muted">Single-use codes. Enter one at checkout (one code per order, no stacking).</p>
              <div data-rw-voucher-list></div>
            </div>

            <details class="rw__earn">
              <summary>How to earn Paw Points</summary>
              <ul class="rw__earn-list">
                <li><strong>1 point per $1</strong> on every online order. Sign in before checkout so every crumb counts.</li>
                <li><strong>+25 welcome bonus</strong> for joining Kat's Rewards.</li>
                <li><strong>Double Paw Points days</strong> at markets, flavor drops, and National Cookie Day.</li>
              </ul>
              <p class="rw-muted rw__earn-note">Points post once your order is paid and bank up. They never reset.</p>
            </details>

            <div class="rw__block">
              <div class="rw__block-head">
                <h4 class="rw__block-title">Recent activity</h4>
                <button type="button" class="rw-linkbtn rw__showmore" data-rw-action="open-activity" data-rw-activity-more hidden>Show more</button>
              </div>
              <div data-rw-activity></div>
            </div>
          </div>

          <!-- REDEEM SCREEN: full ladder of rewards to cash points in. The
               centered page header (title + back) is rendered by the host
               account modal (custom-account-menu), matching its other pages. -->
          <div class="rw__screen" data-rw-screen="redeem" hidden>
            <p class="rw-muted">Points bank up and never reset. Cash in now or save for the Golden Cookie.</p>
            <div class="rw__ladder" data-rw-ladder></div>
          </div>

          <!-- ACTIVITY SCREEN: the full points-activity history (header from host) -->
          <div class="rw__screen" data-rw-screen="activity" hidden>
            <div data-rw-activity-all></div>
          </div>
        </div>

        <!-- Inline redeem sheet (confirm → code reveal). Absolutely positioned
             over the module so it never relies on the browser top layer, which
             the header popover already occupies. -->
        <div class="rw__overlay" data-rw-overlay hidden>
          <div class="rw__sheet">
            <div data-rw-confirm>
              <div class="rw__sheet-cookie">${this.cookieImg(56)}</div>
              <h4 class="rw__sheet-title" data-rw-confirm-title>Redeem?</h4>
              <p class="rw-muted" data-rw-confirm-msg></p>
              <p class="rw-error" data-rw-redeem-error hidden></p>
              <div class="rw__sheet-actions">
                <button type="button" class="rw-btn rw-btn--ghost" data-rw-action="confirm-cancel">Not yet</button>
                <button type="button" class="rw-btn rw-btn--primary" data-rw-action="confirm-go">Redeem</button>
              </div>
              <button type="button" class="rw-linkbtn" data-rw-action="redeem-retry" hidden>Retry</button>
            </div>

            <div data-rw-reveal hidden>
              <div class="rw__sheet-cookie">${this.cookieImg(56)}</div>
              <h4 class="rw__sheet-title">Reward unlocked!</h4>
              <p class="rw-muted" data-rw-reveal-desc></p>
              <div class="rw__code" data-rw-reveal-code>PAW-––––––</div>
              <p class="rw-muted rw__code-note" data-rw-reveal-note></p>
              <div class="rw__sheet-actions">
                <button type="button" class="rw-btn rw-btn--outline" data-rw-action="copy" data-rw-reveal-copy>Copy code</button>
                <a class="rw-btn rw-btn--primary" data-rw-reveal-apply href="#">Apply to cart</a>
              </div>
              <button type="button" class="rw-linkbtn" data-rw-action="reveal-done">Done</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('rewards-account')) {
  customElements.define('rewards-account', RewardsAccount);
}
